// contexts/SystemAdminContext.ts
import { createContext, useContext } from 'react';
import type { AppUser } from '@/types/users';
import type { Agency } from '@/types/agencies';
import type { Broadcaster } from '@/types/broadcasters';
import type { AdTech } from '@/types/adtechs';
import type { Channel } from '@/types/channels';
import type { Country } from '@/types/countries';
import type { Taxonomy } from '@/types/taxonomies';
import type { Customer } from '@/types/customers';

export type SystemAdminEntityType =
    | 'broadcasters'
    | 'adtechs'
    | 'channels'
    | 'countries'
    | 'users'
    | 'agencies'
    | 'clients'
    | 'unassociatedAgencies'
    | 'unassociatedClients'
    | 'taxonomies';

export type SystemSaveableEntityType = 'broadcaster' | 'adtech' | 'channel' | 'adtechgroup';
export type SystemDeletableEntityType = 'user' | 'agency' | 'client';

export interface SystemEntityData {
    id?: number;
    [key: string]: unknown;
}

export interface SystemAdminData {
    broadcasters: Broadcaster[];
    channels: Channel[];
    countries: Country[];
    agencies: Agency[];
    clients: Customer[];
    unassociatedAgencies: Agency[];
    unassociatedClients: Customer[];
    users: AppUser[];
    taxonomies: Taxonomy[];
    adtechs: AdTech[];
    adtechgroups: unknown[];
}

export interface SystemAdminContextValue {
    systemAdminData: SystemAdminData;
    loading: boolean;
    fetchData: (entityType: SystemAdminEntityType) => Promise<unknown>;
    handleSaveEntity: (
        entityData: SystemEntityData,
        entityType: SystemSaveableEntityType,
        onSuccess?: () => void
    ) => Promise<void>;
    handleDeleteEntity: (entityId: number, entityType: SystemDeletableEntityType) => Promise<void>;
    handleSaveDirectUser: (userData: Record<string, unknown>, onSuccess?: () => void) => Promise<void>;
    handleReassignUser: (
        userId: number,
        entityTypeId: number,
        entityId: number,
        onSuccess?: () => void
    ) => Promise<void>;
    handleSaveRelationship: (relationshipData: Record<string, unknown>, onSuccess?: () => void) => Promise<void>;
    handleSaveBroadcaster: (data: SystemEntityData, onSuccess?: () => void) => Promise<void>;
    handleSaveAdTech: (data: SystemEntityData, onSuccess?: () => void) => Promise<void>;
    handleSaveChannel: (data: SystemEntityData, onSuccess?: () => void) => Promise<void>;
}

export const SystemAdminContext = createContext<SystemAdminContextValue | null>(null);

export const useSystemAdmin = (): SystemAdminContextValue => {
    const context = useContext(SystemAdminContext);

    if (context === null) {
        throw new Error('useSystemAdmin must be used within a SystemAdminProvider');
    }

    return context;
};
